/**
 * RecoveryLanding — `/auth/recovery?token=...`
 *
 * v266.4 — refactored to use the shared PasswordCreator component so
 * the interactive password UX stays in lockstep between Signup and
 * Reset. This file now owns only the token handoff, submit call,
 * and success/error state — all password rule/meter/match logic
 * lives in `<PasswordCreator/>`.
 */
import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, KeyRound, ArrowRight, Mail, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import PasswordCreator from '@/components/PasswordCreator';

export default function RecoveryLanding() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [pwd, setPwd] = useState('');
  const [pwd2, setPwd2] = useState('');
  const [valid, setValid] = useState(false);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState('form'); // 'form' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [resetEmail, setResetEmail] = useState('');

  const token = params.get('token');

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      setState('error');
      setErrorMsg('Missing token in URL. Please request a new reset link.');
      return;
    }
    if (!valid || busy) return;
    setBusy(true);
    try {
      const { data } = await api.post('/auth/recovery/verify', {
        token, new_password: pwd,
      });
      setResetEmail(data.email);
      setState('success');
      toast.success('Password reset. You can sign in now.');
      setTimeout(() => {
        nav(`/login?reset=1&email=${encodeURIComponent(data.email)}`);
      }, 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && /breach|data breach/i.test(detail)) {
        toast.error(detail);
      } else {
        setState('error');
        setErrorMsg(detail || 'Reset failed. Request a new link.');
      }
    } finally { setBusy(false); }
  };

  const strongEnough = valid && pwd.length >= 12;

  return (
    <main
      data-testid="recovery-landing"
      className="relative min-h-screen overflow-hidden text-white flex items-center justify-center px-4 py-12"
      style={{
        background:
          'radial-gradient(1200px 600px at 80% -10%, rgba(174,82,234,0.14), transparent 60%),' +
          'radial-gradient(900px 600px at -10% 110%, rgba(139,63,197,0.12), transparent 60%),' +
          'linear-gradient(180deg, #050505 0%, #0A0A0A 50%, #050505 100%)',
      }}
    >
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.06]"
        style={{
          backgroundImage:
            'linear-gradient(rgba(174,82,234,0.3) 1px, transparent 1px),' +
            'linear-gradient(90deg, rgba(174,82,234,0.3) 1px, transparent 1px)',
          backgroundSize: '32px 32px',
        }}
      />
      <div
        className="relative rounded-2xl border bg-white/[0.04] backdrop-blur-xl p-7 lg:p-9 max-w-md w-full transition-all duration-500"
        style={{
          borderColor: strongEnough ? 'rgba(174,82,234,0.45)' : 'rgba(255,255,255,0.10)',
          boxShadow: strongEnough
            ? '0 30px 120px -30px rgba(174,82,234,0.6), 0 0 0 1px rgba(174,82,234,0.15) inset'
            : '0 30px 120px -40px rgba(174,82,234,0.4)',
        }}
      >

        {state === 'form' && (
          <div data-testid="recovery-form">
            <div
              className="h-12 w-12 rounded-md flex items-center justify-center border transition-all duration-500"
              style={{
                backgroundColor: 'rgba(174,82,234,0.10)',
                color: '#AE52EA',
                borderColor: 'rgba(174,82,234,0.40)',
                transform: strongEnough ? 'rotate(-6deg) scale(1.05)' : 'rotate(0) scale(1)',
                boxShadow: strongEnough ? '0 0 24px -4px rgba(174,82,234,0.6)' : 'none',
              }}
            >
              {strongEnough ? <ShieldCheck size={22} /> : <KeyRound size={22} />}
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
              [SET · NEW PASSWORD]
            </div>
            <h1 className="display text-3xl mt-1.5">Pick a strong password.</h1>
            <p className="text-xs text-white/55 mt-2 leading-relaxed">
              We&apos;ll sign you back in the moment it&apos;s saved. Your rules light up green as you type — the button will unlock when everything&apos;s good.
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4">
              <PasswordCreator
                value={pwd}
                onValueChange={setPwd}
                confirmValue={pwd2}
                onConfirmChange={setPwd2}
                onValidChange={setValid}
                variant="dark"
                idPrefix="recovery"
                passwordLabel="New password"
                confirmLabel="Confirm password"
                autoFocus
                disabled={busy}
              />

              <button
                type="submit"
                disabled={!valid || busy}
                data-testid="recovery-submit"
                className="group relative w-full inline-flex items-center justify-center gap-2 rounded-md text-sm font-semibold px-4 py-2.5 transition-all duration-300 overflow-hidden"
                style={{
                  background: (valid && !busy)
                    ? 'linear-gradient(90deg, #8B3FC5 0%, #AE52EA 60%, #8B3FC5 100%)'
                    : 'rgba(255,255,255,0.06)',
                  color: (valid && !busy) ? '#ffffff' : 'rgba(255,255,255,0.35)',
                  cursor: (valid && !busy) ? 'pointer' : 'not-allowed',
                  boxShadow: (valid && !busy)
                    ? '0 0 36px -4px rgba(174,82,234,0.7), 0 0 0 1px rgba(174,82,234,0.35) inset'
                    : 'none',
                  backgroundSize: (valid && !busy) ? '200% 100%' : '100% 100%',
                  animation: (valid && !busy) ? 've-shimmer 3s ease-in-out infinite' : 'none',
                  border: (valid && !busy) ? '1px solid rgba(174,82,234,0.5)' : '1px solid rgba(255,255,255,0.08)',
                }}
              >
                {busy ? 'Saving…' : valid ? 'Reset password' : 'Fill in a valid password'}
                {valid && !busy && (
                  <ArrowRight size={14} className="transition-transform group-hover:translate-x-0.5" />
                )}
              </button>

              <p className="text-[10px] text-white/40 leading-relaxed">
                After saving, we&apos;ll sign out any old sessions on this account for safety.
              </p>
            </form>
          </div>
        )}

        {state === 'success' && (
          <div data-testid="recovery-success">
            <div
              className="h-12 w-12 rounded-md flex items-center justify-center border"
              style={{
                backgroundColor: 'rgba(16,185,129,0.10)',
                color: '#10B981',
                borderColor: 'rgba(16,185,129,0.40)',
              }}
            >
              <CheckCircle2 size={22} />
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-emerald-400 font-bold">
              [PASSWORD · UPDATED]
            </div>
            <h1 className="display text-2xl mt-1.5">Done — you&apos;re all set.</h1>
            <p className="text-sm text-white/65 mt-3">
              Taking you to sign in as <span className="font-mono text-white/90">{resetEmail}</span>…
            </p>
          </div>
        )}

        {state === 'error' && (
          <div data-testid="recovery-error">
            <div
              className="h-12 w-12 rounded-md flex items-center justify-center border"
              style={{
                backgroundColor: 'rgba(250,176,5,0.10)',
                color: '#F59E0B',
                borderColor: 'rgba(250,176,5,0.40)',
              }}
            >
              <AlertTriangle size={22} />
            </div>
            <div className="mt-4 text-[10px] uppercase tracking-[0.22em] text-amber-400 font-bold">
              [LINK · INVALID]
            </div>
            <h1 className="display text-2xl mt-1.5">Can&apos;t reset.</h1>
            <p className="text-sm text-white/65 mt-3">{errorMsg}</p>
            <div className="mt-6 flex flex-wrap gap-2">
              <Link
                to="/forgot-password"
                data-testid="recovery-request-new"
                className="inline-flex items-center gap-2 rounded-md bg-[#8B3FC5] text-white text-[11px] font-bold px-4 py-2 uppercase tracking-[0.22em] hover:bg-[#AE52EA] transition-colors"
              >
                <Mail size={12} />
                Request a new link
              </Link>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 rounded-md border border-white/15 text-white/80 text-[11px] font-bold px-4 py-2 uppercase tracking-[0.22em] hover:bg-white/5 transition-colors"
              >
                Back to sign in
                <ArrowRight size={12} />
              </Link>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes ve-shimmer {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </main>
  );
}
